import { css } from '@emotion/react';
import { GradientBg } from './mixins/_gradients.mixin';
import { BoxShadow } from './mixins/_box-shadow.mixin';
import { math } from 'polished';
import { BorderRadius } from './mixins/_border-radius.mixin';
import { add, escapeSVG } from './_functions';
import { Transition } from './mixins/_transition.mixin';
import { FontSize } from './vendor/_rfs.mixin';

export default $ => {
  const rules = [];

  // Checkboxes and radios
  //
  // Base class takes care of all the key behavioral aspects.
  rules.push(css`
    .custom-control {
      position: relative;
      z-index: 1;
      display: block;
      min-height: ${math(`${$.fontSizeBase} * ${$.lineHeightBase}`)};
      padding-left: ${math(`${$.customControlGutter} + ${$.customControlIndicatorSize}`)};
      color-adjust: exact; // Keep themed appearance for print
    }
  `);

  rules.push(css`
    .custom-control-inline {
      display: inline-flex;
      margin-right: ${$.customControlSpacerX};
    }
  `);

  rules.push(css`
    .custom-control-input {
      position: absolute;
      left: 0;
      z-index: -1; // Put the input behind the label so it doesn't overlay text
      width: ${$.customControlIndicatorSize};
      height: ${math(`(${$.fontSizeBase} * ${$.lineHeightBase} + ${$.customControlIndicatorSize}) * 0.5`)};
      opacity: 0;

      &:checked ~ .custom-control-label::before {
        color: ${$.customControlIndicatorCheckedColor};
        border-color: ${$.customControlIndicatorCheckedBorderColor};
        ${new GradientBg($.customControlIndicatorCheckedBg).include($)};
        // ${new BoxShadow($.customControlIndicatorCheckedBoxShadow).include($)};
      }

      &:focus ~ .custom-control-label::before {
        // the mixin is not used here to make sure there is feedback
        ${
          $.enableShadows
            ? css`
          // box-shadow: ${$.inputBoxShadow}, ${$.customControlIndicatorFocusBoxShadow};
        `
            : css`
          // box-shadow: ${$.customControlIndicatorFocusBoxShadow};
        `
        }
      }

      &:focus:not(:checked) ~ .custom-control-label::before {
        border-color: ${$.customControlIndicatorFocusBorderColor};
      }

      &:not(:disabled):active ~ .custom-control-label::before {
        color: ${$.customControlIndicatorActiveColor};
        background-color: ${$.customControlIndicatorActiveBg};
        border-color: ${$.customControlIndicatorActiveBorderColor};
        // ${new BoxShadow($.customControlIndicatorActiveBoxShadow).include($)};
      }

      // Use [disabled] and :disabled to work around https://github.com/twbs/bootstrap/issues/28247
      &[disabled],
      &:disabled {
        ~ .custom-control-label {
          color: ${$.customControlLabelDisabledColor};

          &::before {
            background-color: ${$.customControlIndicatorDisabledBg};
          }
        }
      }
    }
  `);

  // Custom control indicators
  //
  // Build the custom controls out of pseudo-elements.
  const ccLabelTop = math(`(${$.fontSizeBase} * ${$.lineHeightBase} - ${$.customControlIndicatorSize}) * 0.5`);
  const ccLabelLeft = math(`-(${$.customControlGutter} + ${$.customControlIndicatorSize})`);

  rules.push(css`
    .custom-control-label {
      position: relative;
      margin-bottom: 0;
      color: ${$.customControlLabelColor};
      vertical-align: top;
      cursor: ${$.customControlCursor};

      // Background-color and (when enabled) gradient
      &::before {
        position: absolute;
        top: ${ccLabelTop};
        left: ${ccLabelLeft};
        display: block;
        width: ${$.customControlIndicatorSize};
        height: ${$.customControlIndicatorSize};
        pointer-events: none;
        content: "";
        background-color: ${$.customControlIndicatorBg};
        border: ${$.customControlIndicatorBorderColor} solid ${$.customControlIndicatorBorderWidth};
        // ${new BoxShadow($.customControlIndicatorBoxShadow).include($)};
      }

      // Foreground (icon)
      &::after {
        position: absolute;
        top: ${ccLabelTop};
        left: ${ccLabelLeft};
        display: block;
        width: ${$.customControlIndicatorSize};
        height: ${$.customControlIndicatorSize};
        content: "";
        background: 50% / ${$.customControlIndicatorBgSize} no-repeat;
      }
    }
  `);

  rules.push(css`
    // Checkboxes
    //
    // Tweak just a few things for checkboxes.

    .custom-checkbox {
      .custom-control-label::before {
        ${new BorderRadius($.customCheckboxIndicatorBorderRadius).include($)};
      }

      .custom-control-input:checked ~ .custom-control-label {
        &::after {
          background-image: ${escapeSVG($.customCheckboxIndicatorIconChecked)};
        }
      }

      .custom-control-input:indeterminate ~ .custom-control-label {
        &::before {
          border-color: ${$.customCheckboxIndicatorIndeterminateBorderColor};
          ${new GradientBg($.customCheckboxIndicatorIndeterminateBg).include($)};
          // ${new BoxShadow($.customCheckboxIndicatorIndeterminateBoxShadow).include($)};
        }
        &::after {
          background-image: ${escapeSVG($.customCheckboxIndicatorIconIndeterminate)};
        }
      }

      .custom-control-input:disabled {
        &:checked ~ .custom-control-label::before {
          ${new GradientBg($.customControlIndicatorCheckedDisabledBg).include($)};
        }
        &:indeterminate ~ .custom-control-label::before {
          ${new GradientBg($.customControlIndicatorCheckedDisabledBg).include($)};
        }
      }
    }
  `);

  // Radios
  //
  // Tweak just a few things for radios.
  rules.push(css`
    .custom-radio {
      .custom-control-label::before {
        // stylelint-disable-next-line property-disallowed-list
        border-radius: ${$.customRadioIndicatorBorderRadius};
      }

      .custom-control-input:checked ~ .custom-control-label {
        &::after {
          background-image: ${escapeSVG($.customRadioIndicatorIconChecked)};
        }
      }

      .custom-control-input:disabled {
        &:checked ~ .custom-control-label::before {
          ${new GradientBg($.customControlIndicatorCheckedDisabledBg).include($)};
        }
      }
    }
  `);

  rules.push(css`
    // switches
    //
    // Tweak a few things for switches

    .custom-switch {
      padding-left: ${math(`${$.customSwitchWidth} + ${$.customControlGutter}`)};

      .custom-control-label {
        &::before {
          left: ${math(`-(${$.customSwitchWidth} + ${$.customControlGutter})`)};
          width: ${$.customSwitchWidth};
          pointer-events: all;
          // stylelint-disable-next-line property-disallowed-list
          border-radius: ${$.customSwitchIndicatorBorderRadius};
        }

        &::after {
          top: ${add(
            math(`(${$.fontSizeBase} * ${$.lineHeightBase} - ${$.customControlIndicatorSize}) * 0.5`),
            math(`${$.customControlIndicatorBorderWidth} * 2`)
          )};
          left: ${add(math(`-(${$.customSwitchWidth} + ${$.customControlGutter})`), math(`${$.customControlIndicatorBorderWidth} * 2`))};
          width: ${$.customSwitchIndicatorSize};
          height: ${$.customSwitchIndicatorSize};
          background-color: ${$.customControlIndicatorBorderColor};
          // stylelint-disable-next-line property-disallowed-list
          border-radius: ${$.customSwitchIndicatorBorderRadius};
          ${new Transition(`transform .15s ease-in-out`, $.customFormsTransition).include($)};
        }
      }

      .custom-control-input:checked ~ .custom-control-label {
        &::after {
          background-color: ${$.customControlIndicatorBg};
          transform: translateX(${math(`${$.customSwitchWidth} - ${$.customControlIndicatorSize}`)});
        }
      }

      .custom-control-input:disabled {
        &:checked ~ .custom-control-label::before {
          ${new GradientBg($.customControlIndicatorCheckedDisabledBg).include($)};
        }
      }
    }
  `);

  // Select
  //
  // Replaces the browser default select with a custom one, mostly pulled from
  // https://primer.github.io/.
  //
  rules.push(css`
    .custom-select {
      display: inline-block;
      width: 100%;
      height: ${$.customSelectHeight};
      padding: ${$.customSelectPaddingY} ${math(`${$.customSelectPaddingX} + ${$.customSelectIndicatorPadding}`)} ${$.customSelectPaddingY} ${
    $.customSelectPaddingX
  };
      font-family: ${$.customSelectFontFamily};
      ${new FontSize($.customSelectFontSize).include($)};
      font-weight: ${$.customSelectFontWeight};
      line-height: ${$.customSelectLineHeight};
      color: ${$.customSelectColor};
      vertical-align: middle;
      background: ${$.customSelectBg} ${$.customSelectBackground};
      border: ${$.customSelectBorderWidth} solid ${$.customSelectBorderColor};
      ${new BorderRadius($.customSelectBorderRadius, 0).include($)};
      // ${new BoxShadow($.customSelectBoxShadow).include($)};
      appearance: none;

      &:focus {
        border-color: ${$.customSelectFocusBorderColor};
        outline: 0;
        ${
          $.enableShadows
            ? new BoxShadow($.customSelectBoxShadow, $.customSelectFocusBoxShadow).include($)
            : css`
          // Avoid using mixin so we can pass custom focus shadow properly
          // box-shadow: ${$.customSelectFocusBoxShadow};
        `
        }

        &::-ms-value {
          // For visual consistency with other platforms/browsers,
          // suppress the default white text on blue background highlight given to
          // the selected option text when the (still closed) <select> receives focus
          // in IE and (under certain conditions) Edge.
          // See https://github.com/twbs/bootstrap/issues/19398.
          color: ${$.inputColor};
          background-color: ${$.inputBg};
        }
      }

      &[multiple],
      &[size]:not([size="1"]) {
        height: auto;
        padding-right: ${$.customSelectPaddingX};
        background-image: none;
      }

      &:disabled {
        color: ${$.customSelectDisabledColor};
        background-color: ${$.customSelectDisabledBg};
      }

      // Hides the default caret in IE11
      &::-ms-expand {
        display: none;
      }

      // Remove outline from select box in FF
      &:-moz-focusring {
        color: transparent;
        text-shadow: 0 0 0 ${$.customSelectColor};
      }
    }
  `);

  rules.push(css`
    .custom-select-sm {
      height: ${$.customSelectHeight}-sm;
      padding-top: ${$.customSelectPaddingY}-sm;
      padding-bottom: ${$.customSelectPaddingY}-sm;
      padding-left: ${$.customSelectPaddingX}-sm;
      ${new FontSize($.customSelectFontSizeSm).include($)};
    }
  `);

  rules.push(css`
    .custom-select-lg {
      height: ${$.customSelectHeight}-lg;
      padding-top: ${$.customSelectPaddingY}-lg;
      padding-bottom: ${$.customSelectPaddingY}-lg;
      padding-left: ${$.customSelectPaddingX}-lg;
      ${new FontSize($.customSelectFontSizeLg).include($)};
    }
  `);

  // File
  //
  // Custom file input.
  rules.push(css`
    .custom-file {
      position: relative;
      display: inline-block;
      width: 100%;
      height: ${$.customFileHeight};
      margin-bottom: 0;
    }
  `);

  rules.push(css`
    .custom-file-input {
      position: relative;
      z-index: 2;
      width: 100%;
      height: ${$.customFileHeight};
      margin: 0;
      overflow: hidden;
      opacity: 0;

      &:focus ~ .custom-file-label {
        border-color: ${$.customFileFocusBorderColor};
        // box-shadow: ${$.customFileFocusBoxShadow};
      }

      // Use [disabled] and :disabled to work around https://github.com/twbs/bootstrap/issues/28247
      &[disabled] ~ .custom-file-label,
      &:disabled ~ .custom-file-label {
        background-color: ${$.customFileDisabledBg};
      }

      ${css(
        Object.entries($.customFileText).map(
          ([lang, value]) => css`
            &:lang(${lang}) ~ .custom-file-label::after {
              content: ${value};
            }
          `
        )
      )}

      ~ .custom-file-label[data-browse]::after {
        content: attr(data-browse);
      }
    }
  `);

  rules.push(css`
    .custom-file-label {
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      z-index: 1;
      height: ${$.customFileHeight};
      padding: ${$.customFilePaddingY} ${$.customFilePaddingX};
      overflow: hidden;
      font-family: ${$.customFileFontFamily};
      font-weight: ${$.customFileFontWeight};
      line-height: ${$.customFileLineHeight};
      color: ${$.customFileColor};
      background-color: ${$.customFileBg};
      border: ${$.customFileBorderWidth} solid ${$.customFileBorderColor};
      ${new BorderRadius($.customFileBorderRadius).include($)};
      ${new BoxShadow($.customFileBoxShadow).include($)};

      &::after {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        z-index: 3;
        display: block;
        height: ${$.customFileHeight}-inner;
        padding: ${$.customFilePaddingY} ${$.customFilePaddingX};
        line-height: ${$.customFileLineHeight};
        color: ${$.customFileButtonColor};
        content: 'Browse';
        ${new GradientBg($.customFileButtonBg).include($)};
        border-left: inherit;
        ${new BorderRadius([`0 ${$.customFileBorderRadius} ${$.customFileBorderRadius} 0`]).include($)};
      }
    }
  `);

  // Range
  //
  // Style range inputs the same across browsers. Vendor-specific rules for pseudo
  // elements cannot be mixed. As such, there are no shared styles for focus or
  // active states on prefixed selectors.
  rules.push(css`
    .custom-range {
      width: 100%;
      height: ${add($.customRangeThumbHeight, math(`${$.customRangeThumbFocusBoxShadowWidth} * 2`))};
      padding: 0; // Need to reset padding
      background-color: transparent;
      appearance: none;

      &:focus {
        outline: 0;

        // Pseudo-elements must be split across multiple rulesets to have an effect.
        // No box-shadow() mixin for focus accessibility.
        &::-webkit-slider-thumb { box-shadow: ${$.customRangeThumbFocusBoxShadow}; }
        &::-moz-range-thumb     { box-shadow: ${$.customRangeThumbFocusBoxShadow}; }
        &::-ms-thumb            { box-shadow: ${$.customRangeThumbFocusBoxShadow}; }
      }

      &::-moz-focus-outer {
        border: 0;
      }

      &::-webkit-slider-thumb {
        width: ${$.customRangeThumbWidth};
        height: ${$.customRangeThumbHeight};
        margin-top: ${math(`(${$.customRangeTrackHeight} - ${$.customRangeThumbHeight}) * 0.5`)}; // Webkit specific
        ${new GradientBg($.customRangeThumbBg).include($)};
        border: ${$.customRangeThumbBorder};
        ${new BorderRadius($.customRangeThumbBorderRadius).include($)};
        ${new BoxShadow($.customRangeThumbBoxShadow).include($)};
        ${new Transition($.customFormsTransition).include($)};
        appearance: none;

        &:active {
          ${new GradientBg($.customRangeThumbActiveBg).include($)};
        }
      }

      &::-webkit-slider-runnable-track {
        width: ${$.customRangeTrackWidth};
        height: ${$.customRangeTrackHeight};
        color: transparent; // Why?
        cursor: ${$.customRangeTrackCursor};
        background-color: ${$.customRangeTrackBg};
        border-color: transparent;
        ${new BorderRadius($.customRangeTrackBorderRadius).include($)};
        ${new BoxShadow($.customRangeTrackBoxShadow).include($)};
      }

      &::-moz-range-thumb {
        width: ${$.customRangeThumbWidth};
        height: ${$.customRangeThumbHeight};
        ${new GradientBg($.customRangeThumbBg).include($)};
        border: ${$.customRangeThumbBorder};
        ${new BorderRadius($.customRangeThumbBorderRadius).include($)};
        // ${new BoxShadow($.customRangeThumbBoxShadow).include($)};
        ${new Transition($.customFormsTransition).include($)};
        appearance: none;

        &:active {
          ${new GradientBg($.customRangeThumbActiveBg).include($)};
        }
      }

      &::-moz-range-track {
        width: ${$.customRangeTrackWidth};
        height: ${$.customRangeTrackHeight};
        color: transparent;
        cursor: ${$.customRangeTrackCursor};
        background-color: ${$.customRangeTrackBg};
        border-color: transparent; // Firefox specific?
        ${new BorderRadius($.customRangeTrackBorderRadius).include($)};
        // ${new BoxShadow($.customRangeTrackBoxShadow).include($)};
      }

      &::-ms-thumb {
        width: ${$.customRangeThumbWidth};
        height: ${$.customRangeThumbHeight};
        margin-top: 0; // Edge specific
        margin-right: ${$.customRangeThumbFocusBoxShadowWidth}; // Workaround that overflowed box-shadow is hidden.
        margin-left: ${$.customRangeThumbFocusBoxShadowWidth};  // Workaround that overflowed box-shadow is hidden.
        ${new GradientBg($.customRangeThumbBg).include($)};
        border: ${$.customRangeThumbBorder};
        ${new BorderRadius($.customRangeThumbBorderRadius).include($)};
        // ${new BoxShadow($.customRangeThumbBoxShadow).include($)};
        ${new Transition($.customFormsTransition).include($)};
        appearance: none;

        &:active {
          ${new GradientBg($.customRangeThumbActiveBg).include($)};
        }
      }

      &::-ms-track {
        width: ${$.customRangeTrackWidth};
        height: ${$.customRangeTrackHeight};
        color: transparent;
        cursor: ${$.customRangeTrackCursor};
        background-color: transparent;
        border-color: transparent;
        border-width: ${math(`${$.customRangeThumbHeight} * 0.5`)};
        // ${new BoxShadow($.customRangeTrackBoxShadow).include($)};
      }

      &::-ms-fill-lower {
        background-color: ${$.customRangeTrackBg};
        ${new BorderRadius($.customRangeTrackBorderRadius).include($)};
      }

      &::-ms-fill-upper {
        margin-right: 15px; // arbitrary?
        background-color: ${$.customRangeTrackBg};
        ${new BorderRadius($.customRangeTrackBorderRadius).include($)};
      }

      &:disabled {
        &::-webkit-slider-thumb {
          background-color: ${$.customRangeThumbDisabledBg};
        }

        &::-webkit-slider-runnable-track {
          cursor: default;
        }

        &::-moz-range-thumb {
          background-color: ${$.customRangeThumbDisabledBg};
        }

        &::-moz-range-track {
          cursor: default;
        }

        &::-ms-thumb {
          background-color: ${$.customRangeThumbDisabledBg};
        }
      }
    }
  `);

  rules.push(css`
    .custom-control-label::before,
    .custom-file-label,
    .custom-select {
      ${new Transition($.customFormsTransition).include($)};
    }
  `);

  // rules.push(css`

  return css(rules);
};
